import React, { useState, useEffect } from 'react';
import {
  Settings,
  Shield,
  Bell,
  Database,
  Mail,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff
} from 'lucide-react';

interface SystemSettings {
  general: {
    system_name: string;
    system_description: string;
    timezone: string;
    date_format: string;
    default_language: string;
  };
  email: {
    smtp_host: string;
    smtp_port: string;
    smtp_username: string;
    smtp_password: string;
    from_email: string;
    from_name: string;
  };
  notifications: {
    email_notifications: boolean;
    sms_notifications: boolean;
    push_notifications: boolean;
    approval_notifications: boolean;
    deadline_reminders: boolean;
  };
  security: {
    password_min_length: number;
    password_require_uppercase: boolean;
    password_require_lowercase: boolean;
    password_require_numbers: boolean;
    password_require_symbols: boolean;
    session_timeout: number;
    max_login_attempts: number;
  };
  backup: {
    auto_backup: boolean;
    backup_frequency: string;
    backup_retention_days: number;
    last_backup: string;
  };
}

const SettingsManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [settings, setSettings] = useState<SystemSettings>({
    general: {
      system_name: 'Training Mobilisation System',
      system_description: 'Enterprise training and batch management platform',
      timezone: 'Asia/Kolkata',
      date_format: 'DD/MM/YYYY',
      default_language: 'en'
    },
    email: {
      smtp_host: '',
      smtp_port: '587',
      smtp_username: '',
      smtp_password: '',
      from_email: '',
      from_name: ''
    },
    notifications: {
      email_notifications: true,
      sms_notifications: false,
      push_notifications: true,
      approval_notifications: true,
      deadline_reminders: true
    },
    security: {
      password_min_length: 8,
      password_require_uppercase: true,
      password_require_lowercase: true,
      password_require_numbers: true,
      password_require_symbols: false,
      session_timeout: 3600,
      max_login_attempts: 5
    },
    backup: {
      auto_backup: true,
      backup_frequency: 'daily',
      backup_retention_days: 30,
      last_backup: new Date().toISOString()
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // This would fetch settings from the API
      // For now, we'll use the default settings
      console.log('Fetching system settings...');
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      // This would save settings to the API
      console.log('Saving settings:', settings);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (type: 'email' | 'database') => {
    try {
      console.log(`Testing ${type} connection...`);
      // This would test the connection
      setMessage({ type: 'success', text: `${type} connection successful!` });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to connect to ${type}` });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const performBackup = async () => {
    try {
      console.log('Starting backup...');
      setMessage({ type: 'success', text: 'Backup completed successfully!' });
      setSettings(prev => ({
        ...prev,
        backup: {
          ...prev.backup,
          last_backup: new Date().toISOString()
        }
      }));
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Backup failed. Please try again.' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'email', name: 'Email', icon: Mail },
    { id: 'backup', name: 'Backup', icon: Database },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-600 mt-1">Manage system configuration and preferences</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchSettings}
            disabled={loading}
            className="btn-secondary"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200'
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 mr-2" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-2" />
          )}
          {message.text}
        </div>
      )}

      {/* Settings Interface */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 border-r">
            <nav className="p-4 space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700 border border-primary-200'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-3" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 p-6">
            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        System Name
                      </label>
                      <input
                        type="text"
                        value={settings.general.system_name}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          general: { ...prev.general, system_name: e.target.value }
                        }))}
                        className="input-field"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Timezone
                      </label>
                      <select
                        value={settings.general.timezone}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          general: { ...prev.general, timezone: e.target.value }
                        }))}
                        className="input-field"
                      >
                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">America/New_York (EST)</option>
                        <option value="Europe/London">Europe/London (GMT)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date Format
                      </label>
                      <select
                        value={settings.general.date_format}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          general: { ...prev.general, date_format: e.target.value }
                        }))}
                        className="input-field"
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Language
                      </label>
                      <select
                        value={settings.general.default_language}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          general: { ...prev.general, default_language: e.target.value }
                        }))}
                        className="input-field"
                      >
                        <option value="en">English</option>
                        <option value="hi">Hindi</option>
                        <option value="mr">Marathi</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      System Description
                    </label>
                    <textarea
                      value={settings.general.system_description}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        general: { ...prev.general, system_description: e.target.value }
                      }))}
                      className="input-field"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h3>
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Password Policy</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Minimum Length
                          </label>
                          <input
                            type="number"
                            value={settings.security.password_min_length}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              security: { ...prev.security, password_min_length: parseInt(e.target.value) }
                            }))}
                            className="input-field"
                            min="6"
                            max="20"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Session Timeout (seconds)
                          </label>
                          <input
                            type="number"
                            value={settings.security.session_timeout}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              security: { ...prev.security, session_timeout: parseInt(e.target.value) }
                            }))}
                            className="input-field"
                            min="300"
                            max="86400"
                          />
                        </div>
                      </div>
                      
                      <div className="mt-4 space-y-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.security.password_require_uppercase}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              security: { ...prev.security, password_require_uppercase: e.target.checked }
                            }))}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Require uppercase letters</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.security.password_require_lowercase}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              security: { ...prev.security, password_require_lowercase: e.target.checked }
                            }))}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Require lowercase letters</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.security.password_require_numbers}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              security: { ...prev.security, password_require_numbers: e.target.checked }
                            }))}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Require numbers</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.security.password_require_symbols}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              security: { ...prev.security, password_require_symbols: e.target.checked }
                            }))}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Require special characters</span>
                        </label>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Login Security</h4>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Login Attempts
                        </label>
                        <input
                          type="number"
                          value={settings.security.max_login_attempts}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            security: { ...prev.security, max_login_attempts: parseInt(e.target.value) }
                          }))}
                          className="input-field"
                          min="3"
                          max="10"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Settings */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h3>
                  <div className="space-y-4">
                    <label className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Email Notifications</div>
                        <div className="text-sm text-gray-500">Receive notifications via email</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.email_notifications}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, email_notifications: e.target.checked }
                        }))}
                        className="toggle"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">SMS Notifications</div>
                        <div className="text-sm text-gray-500">Receive notifications via SMS</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.sms_notifications}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, sms_notifications: e.target.checked }
                        }))}
                        className="toggle"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Approval Notifications</div>
                        <div className="text-sm text-gray-500">Notify when approvals are required</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.approval_notifications}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, approval_notifications: e.target.checked }
                        }))}
                        className="toggle"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Deadline Reminders</div>
                        <div className="text-sm text-gray-500">Receive deadline reminders</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.deadline_reminders}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, deadline_reminders: e.target.checked }
                        }))}
                        className="toggle"
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Email Settings */}
            {activeTab === 'email' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Email Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMTP Host
                      </label>
                      <input
                        type="text"
                        value={settings.email.smtp_host}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          email: { ...prev.email, smtp_host: e.target.value }
                        }))}
                        className="input-field"
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMTP Port
                      </label>
                      <input
                        type="text"
                        value={settings.email.smtp_port}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          email: { ...prev.email, smtp_port: e.target.value }
                        }))}
                        className="input-field"
                        placeholder="587"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username
                      </label>
                      <input
                        type="text"
                        value={settings.email.smtp_username}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          email: { ...prev.email, smtp_username: e.target.value }
                        }))}
                        className="input-field"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={settings.email.smtp_password}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            email: { ...prev.email, smtp_password: e.target.value }
                          }))}
                          className="input-field pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Email
                      </label>
                      <input
                        type="email"
                        value={settings.email.from_email}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          email: { ...prev.email, from_email: e.target.value }
                        }))}
                        className="input-field"
                        placeholder="noreply@example.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Name
                      </label>
                      <input
                        type="text"
                        value={settings.email.from_name}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          email: { ...prev.email, from_name: e.target.value }
                        }))}
                        className="input-field"
                        placeholder="Training System"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <button
                      onClick={() => testConnection('email')}
                      className="btn-secondary"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Test Email Connection
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Backup Settings */}
            {activeTab === 'backup' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Backup Configuration</h3>
                  <div className="space-y-6">
                    <label className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Automatic Backup</div>
                        <div className="text-sm text-gray-500">Enable automatic database backups</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.backup.auto_backup}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          backup: { ...prev.backup, auto_backup: e.target.checked }
                        }))}
                        className="toggle"
                      />
                    </label>
                    
                    {settings.backup.auto_backup && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Backup Frequency
                          </label>
                          <select
                            value={settings.backup.backup_frequency}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              backup: { ...prev.backup, backup_frequency: e.target.value }
                            }))}
                            className="input-field"
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Retention Period (days)
                          </label>
                          <input
                            type="number"
                            value={settings.backup.backup_retention_days}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              backup: { ...prev.backup, backup_retention_days: parseInt(e.target.value) }
                            }))}
                            className="input-field"
                            min="7"
                            max="365"
                          />
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Backup Status</h4>
                      <div className="text-sm text-gray-600">
                        <div>Last backup: {new Date(settings.backup.last_backup).toLocaleString()}</div>
                        <div>Next backup: {settings.backup.auto_backup ? 'Scheduled' : 'Manual only'}</div>
                      </div>
                      
                      <div className="mt-4">
                        <button
                          onClick={performBackup}
                          className="btn-primary"
                        >
                          <Database className="w-4 h-4 mr-2" />
                          Create Backup Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;
